import React, { useState, useEffect } from 'react'
import { Table, Button, Form, Alert } from 'react-bootstrap'

import { inject, observer } from 'mobx-react'
import { ContingentRows } from '../../ProductsOverview/Structure'
import SelectedAmountsRow from './SelectedAmountsRow'
import { t } from 'i18next'
import styled from 'styled-components'

const BulkReasonBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin: 0 0 16px 0;
  flex-wrap: wrap;
  justify-content: flex-end;

  .bulk-select {
    min-width: 320px;
  }
  @media (max-width: 576px) {
    flex-direction: column;
    .applyButton {
      width: 100%;
    }
    .bulk-select {
      width: 100%;
    }
  }
`

const StyledHeaderTr = styled.tr`
  th {
    .pending-delivery-info {
      font-size: small;
      color: gray;
      font-weight: initial;
    }
  }
`
const ResponsiveTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;

  @media (max-width: 767px) {
    .react-bs-table-no-data {
      display: none;
    }

    table {
      empty-cells: show;
      width: 100%;
    }

    thead > tr > th {
      width: 50%;
      display: inline-block;
      border: 0.5px solid gray;
      text-align: center;
    }

    .fullWidthMobile {
      width: 100%;
      display: inline-block;
    }

    // tbody,
    // td,
    // tr {
    //   display: block;
    // }

    th::after {
      display: none;
    }

    tr:nth-child(2n) {
      background: #ccc;
    }

    td {
      border: none;
      border-bottom: 1px solid #eee;
      empty-cells: show !important;
      position: relative;
      white-space: normal;
      text-align: left;
    }

    td::before {
      position: absolute;
      top: 6px;
      left: 6px;
      width: 44%;
      white-space: nowrap;
      text-align: left;
      font-weight: bold;
    }

    .actions-header-cell,
    .checkBox-header-cell {
      display: none;
    }

    .actions-cell {
      width: 100% !important;
    }
  }
`

const SelectedAmounts = inject('inventoryStore')(
  observer((props) => {
    const { inventoryStore } = props
    const [bulkReasonId, setBulkReasonId] = useState('')
    const [overwriteReasons, setOverwriteReasons] = useState(false)
    const [appliedInfo, setAppliedInfo] = useState('')

    const handleApplyBulkReason = () => {
      const appliedCount = inventoryStore.applyReasonToAllRows(bulkReasonId, {
        overwrite: overwriteReasons,
      })

      setAppliedInfo(
        appliedCount
          ? t('inventory:bulkApplySuccess', { count: appliedCount })
          : t('inventory:bulkApplyNothing')
      )

      // Optional: auto-hide message
      setTimeout(() => setAppliedInfo(''), 2500)
    }

    const hasPositions = (position) =>
      position.rows &&
      (position.rows.length || inventoryStore.actionType === 'assets')
    const showContingent = (contingent) =>
      contingent.categories.some((category) =>
        category.articles.some((article) =>
          article.positions.some((position) => hasPositions(position))
        )
      )
    const showCategory = (category) =>
      category.articles.some((article) =>
        article.positions.some((position) => hasPositions(position))
      )
    const showArticle = (article) =>
      article.positions.some((position) => hasPositions(position))
    const showPosition = (position) => hasPositions(position)

    let fakeCatalogArticlesContingent = {
      displayName: t('other'),
      categories: [{ articles: inventoryStore.editCatalogArticles }],
    }
    const hasAnyRowsToApply = () => {
      const hasRowsInArticles = (articles = []) =>
        articles.some((article) =>
          (article.positions || []).some(
            (position) => (position.rows?.length ?? 0) > 0
          )
        )

      const hasRowsInContingents = (contingents = []) =>
        contingents.some((contingent) =>
          (contingent.categories || []).some((category) =>
            hasRowsInArticles(category.articles)
          )
        )

      return (
        hasRowsInContingents(inventoryStore.contingents || []) ||
        hasRowsInArticles(inventoryStore.editCatalogArticles || [])
      )
    }

    const showBulkReasons =
      (inventoryStore.actionType === 'returnBookings' ||
        inventoryStore.actionType === 'inventoryBookings') &&
      inventoryStore.returnReasons &&
      inventoryStore.returnReasons.length &&
      hasAnyRowsToApply()

    useEffect(() => {
      if (showBulkReasons) {
        setBulkReasonId('')
        setOverwriteReasons(false)
        setAppliedInfo('')
      }
    }, [showBulkReasons])

    return (
      <>
        {showBulkReasons ? (
          <>
            <BulkReasonBar>
              <div className='bulk-select'>
                <Form.Label className='mb-1'>
                  {t('inventory:bulkReason')}
                </Form.Label>
                <Form.Select
                  value={bulkReasonId}
                  onChange={(e) => setBulkReasonId(e.target.value)}
                >
                  <option value=''>{t('inventory:selectReasons')}</option>
                  {inventoryStore.returnReasons.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.displayName}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <Button
                className='applyButton'
                variant='primary'
                disabled={!bulkReasonId}
                onClick={handleApplyBulkReason}
              >
                {t('inventory:applyReason')}
              </Button>

              <Form.Check
                type='checkbox'
                label={t('inventory:overwriteReason')}
                checked={overwriteReasons}
                onChange={(e) => setOverwriteReasons(e.target.checked)}
              />
            </BulkReasonBar>

            {appliedInfo ? (
              <Alert variant='info' className='py-2'>
                {appliedInfo}
              </Alert>
            ) : null}
          </>
        ) : null}

        {inventoryStore.contingents ? (
          <ResponsiveTableWrapper>
            <Table borderless>
              <tbody>
                {/* Contingent Articles */}
                {inventoryStore.contingents.map((contingent) => {
                  if (!showContingent(contingent)) {
                    return null
                  }
                  return (
                    <ContingentRows
                      key={contingent.id}
                      contingent={contingent}
                      withoutImg
                    >
                      {contingent.categories.map((category) => {
                        if (!showCategory(category)) {
                          return null
                        }
                        return (
                          <React.Fragment key={category.id}>
                            <tr data-id={`category-${category.id}`}>
                              <td colSpan='5'>
                                <h2 className='mt-3'>{category.displayName}</h2>
                              </td>
                            </tr>
                            <StyledHeaderTr>
                              {inventoryStore.actionType === 'assets' && (
                                <th></th>
                              )}
                              <th>{t('name')}</th>
                              <th>{t('inventory:articleNr')}</th>
                              <th>{t('size')}</th>
                              {inventoryStore.actionType === 'assets' && (
                                <th>Max</th>
                              )}
                              <th>
                                {t('quantity')}
                                {inventoryStore.actionType === 'assets' && (
                                  <div className='pending-delivery-info'>
                                    * {t('inventory:pendingDelivery')}
                                  </div>
                                )}
                              </th>
                            </StyledHeaderTr>
                            {category.articles.map((article) => {
                              if (!showArticle(article)) {
                                return null
                              }
                              return article.positions.map((position) => {
                                if (!showPosition(position)) {
                                  return null
                                }
                                return (
                                  <SelectedAmountsRow
                                    key={`${article.id}_${position.articleVariant
                                        ? position.articleVariant.id
                                        : '_'
                                      }`}
                                    article={article}
                                    position={position}
                                  />
                                )
                              })
                            })}
                          </React.Fragment>
                        )
                      })}
                    </ContingentRows>
                  )
                })}
                {/* Catalog Articles */}
                {showContingent(fakeCatalogArticlesContingent) ? (
                  <>
                    <ContingentRows
                      contingent={fakeCatalogArticlesContingent}
                      withoutImg
                    ></ContingentRows>
                    {inventoryStore.editCatalogArticles.map((article) =>
                      article.positions.map((position) => {
                        if (!showPosition(position)) {
                          return null
                        }
                        return (
                          <SelectedAmountsRow
                            key={`${article.id}_${position.articleVariant
                                ? position.articleVariant.id
                                : '_'
                              }`}
                            article={article}
                            position={position}
                          />
                        )
                      })
                    )}
                  </>
                ) : null}
              </tbody>
            </Table>
          </ResponsiveTableWrapper>
        ) : null}
      </>
    )
  })
)

export default SelectedAmounts
